import type { IntakeSpec, ArchitectureSiteDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): ArchitectureSiteDetails {
  return intake.details as ArchitectureSiteDetails;
}

function scaleNumber(scaleRatio: string): number {
  // e.g., '1:500' -> 500
  return parseInt(scaleRatio.split(':')[1], 10);
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const scale = scaleNumber(d.scaleRatio);

  // Real-world site dimensions in mm
  const realSiteLength = intake.core.size?.length ?? 50000; // 50m default
  const realSiteWidth = intake.core.size?.width ?? 50000;   // 50m default
  const groundThickness = 3; // model material thickness, NOT scaled

  const modelSiteLength = realSiteLength / scale;
  const modelSiteWidth = realSiteWidth / scale;

  const out: Part[] = [];

  // Ground panel
  out.push({
    id: 'ground',
    name: '地盤',
    kind: 'panel',
    materialId: 'mat-primary',
    width: modelSiteLength,
    depth: modelSiteWidth,
    thickness: groundThickness,
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    tags: ['ground'],
    paramRefs: [],
    cutouts: [],
  });

  // Building stubs arranged in a grid
  const buildingCount = d.buildingCount;
  const stubW = 8000 / scale;
  const stubD = 8000 / scale;
  const stubH = 9000 / scale;

  const cols = Math.ceil(Math.sqrt(buildingCount));
  const rows = Math.ceil(buildingCount / cols);
  const spacingX = modelSiteLength / (cols + 1);
  const spacingY = modelSiteWidth / (rows + 1);

  let placed = 0;
  outer: for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (placed >= buildingCount) break outer;
      const bx = -(modelSiteLength / 2) + spacingX * (col + 1);
      const by = -(modelSiteWidth / 2) + spacingY * (row + 1);
      out.push({
        id: `building_${placed + 1}`,
        name: `建物 ${placed + 1}`,
        kind: 'custom',
        materialId: 'mat-primary',
        openscadSnippet: `// building stub ${placed + 1}`,
        boundingBox: { w: stubW, d: stubD, h: stubH },
        position: [bx, by, groundThickness],
        rotation: [0, 0, 0],
        tags: ['building'],
        paramRefs: [],
      });
      placed++;
    }
  }

  // Optional road network
  if (d.showRoads) {
    out.push({
      id: 'road_network',
      name: '道路ネットワーク',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// road network stub',
      boundingBox: { w: modelSiteLength, d: 2000 / scale, h: 0.5 },
      position: [0, 0, groundThickness],
      rotation: [0, 0, 0],
      tags: ['road'],
      paramRefs: [],
    });
  }

  // Optional vegetation stub
  if (d.showVegetation) {
    out.push({
      id: 'vegetation_stub',
      name: '植栽（プレースホルダー）',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// vegetation stub',
      boundingBox: { w: 1000 / scale, d: 1000 / scale, h: 5000 / scale },
      position: [modelSiteLength / 4, modelSiteWidth / 4, groundThickness],
      rotation: [0, 0, 0],
      tags: ['vegetation'],
      paramRefs: [],
    });
  }

  // Optional topography
  if (d.showTopography) {
    out.push({
      id: 'topography',
      name: '地形',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// topography heightmap stub',
      boundingBox: { w: modelSiteLength, d: modelSiteWidth, h: 5000 / scale },
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['topography'],
      paramRefs: [],
    });
  }

  return out;
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(_intake: IntakeSpec): Feature[] {
  return [];
}
